import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Mic, MicOff, Loader2, X } from 'lucide-react'
import { useVoice, VoiceState } from '@/hooks/useVoice'
import { useChatStore, MessageStatus } from '@/store/chatStore'
import { NikiAvatar } from '@/components/niki/NikiAvatar'
import { cn } from '@/utils'

export function VoiceButton() {
  // Get chat store functions
  const { addMessage, updateMessage, currentConversationId } = useChatStore()

  const {
    state,
    error,
    transcript,
    isSupported,
    startListening,
    stopListening,
  } = useVoice({
    onTranscript: (userTranscript) => {
      console.log('[Voice] User said:', userTranscript)
    },
    onResponse: (aiResponse) => {
      console.log('[Voice] Niki said:', aiResponse)
    },
    onError: (err) => {
      console.error('[Voice] Error:', err)
    },
    // Pass chat integration callbacks
    onAddUserMessage: (content: string) => {
      const messageId = addMessage({
        role: 'user',
        content,
        status: 'sending' as MessageStatus,
        conversationId: currentConversationId || undefined,
      })
      return messageId
    },
    onUpdateMessageStatus: (messageId: string, status: 'sending' | 'sent' | 'delivered' | 'error') => {
      updateMessage(messageId, { status: status as MessageStatus })
    },
    conversationId: currentConversationId,
  })

  const isActive = state !== 'idle' && state !== 'error'

  const handleClick = () => {
    if (!isSupported) {
      console.error('[Voice] Not supported in this browser')
      return
    }
    if ('vibrate' in navigator) {
      navigator.vibrate(50)
    }
    if (isActive) {
      stopListening()
    } else {
      startListening()
    }
  }

  return (
    <div className="relative">
      {/* Main Button */}
      <motion.button
        onClick={handleClick}
        disabled={state === 'connecting'}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className={cn(
          'relative w-12 h-12 rounded-xl flex items-center justify-center transition-all',
          isActive
            ? 'bg-danger text-white shadow-lg shadow-danger/40'
            : 'glass text-white hover:bg-white/15'
        )}
      >
        <AnimatePresence mode="wait">
          {state === 'connecting' ? (
            <motion.div
              key="connecting"
              initial={{ opacity: 0, rotate: 0 }}
              animate={{ opacity: 1, rotate: 360 }}
              exit={{ opacity: 0 }}
              transition={{ rotate: { duration: 1, repeat: Infinity, ease: 'linear' } }}
            >
              <Loader2 className="w-5 h-5" />
            </motion.div>
          ) : isActive ? (
            <motion.div
              key="active"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
            >
              <MicOff className="w-5 h-5" />
            </motion.div>
          ) : (
            <motion.div
              key="idle"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
            >
              <Mic className="w-5 h-5" />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Pulsing ring when active */}
        {isActive && (
          <motion.div
            className="absolute inset-0 rounded-xl border-2 border-danger"
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.8, 0, 0.8],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
            }}
          />
        )}
      </motion.button>

      {/* Voice Overlay - rendered via Portal */}
      <VoiceOverlay
        isActive={isActive}
        state={state}
        transcript={transcript}
        onStop={stopListening}
      />

      {/* Error tooltip */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
            className="absolute top-full mt-2 left-1/2 -translate-x-1/2 px-3 py-2 rounded-lg text-xs whitespace-nowrap z-50"
            style={{
              background: 'rgba(239, 68, 68, 0.9)',
              backdropFilter: 'blur(8px)',
            }}
          >
            {error}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

interface VoiceOverlayProps {
  isActive: boolean
  state: VoiceState
  transcript: string
  onStop: () => void
}

function VoiceOverlay({ isActive, state, transcript, onStop }: VoiceOverlayProps) {
  const [mounted, setMounted] = useState(false)

  // Ensure we only render in browser (for SSR compatibility)
  useEffect(() => {
    setMounted(true)
    return () => setMounted(false)
  }, [])

  const getAvatarState = () => {
    if (state === 'listening') return 'listening'
    if (state === 'processing') return 'thinking'
    if (state === 'speaking') return 'speaking'
    return 'idle'
  }

  // Use Portal to render outside any stacking context issues
  if (!mounted) return null

  return createPortal(
    <AnimatePresence>
      {isActive && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 flex flex-col items-center justify-center"
          style={{
            zIndex: 99999,
            background: 'linear-gradient(135deg, rgba(26, 77, 19, 0.98) 0%, rgba(8, 31, 8, 0.98) 100%)',
            backdropFilter: 'blur(20px)',
          }}
        >
          {/* Close button */}
          <motion.button
            onClick={onStop}
            className="absolute top-6 right-6 p-3 rounded-xl glass text-white/60 hover:text-white transition-colors"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <X className="w-6 h-6" />
          </motion.button>

          {/* Avatar with state */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1, type: 'spring' }}
          >
            <NikiAvatar size="hero" state={getAvatarState()} />
          </motion.div>

          {/* State Label */}
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-8 text-lg text-white/70"
          >
            {state === 'listening' && 'Listening...'}
            {state === 'processing' && 'Processing...'}
            {state === 'speaking' && 'Niki is speaking...'}
            {state === 'connecting' && 'Connecting...'}
          </motion.p>

          {/* Audio wave visualization - signature green */}
          {state === 'listening' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex items-center gap-1.5 mt-6"
            >
              {[...Array(9)].map((_, i) => (
                <motion.div
                  key={i}
                  className="w-1.5 rounded-full"
                  style={{ backgroundColor: '#43B02A' }}
                  animate={{
                    height: [8, 40, 8],
                    opacity: [0.5, 1, 0.5],
                  }}
                  transition={{
                    duration: 0.5,
                    repeat: Infinity,
                    delay: i * 0.08,
                    ease: 'easeInOut',
                  }}
                />
              ))}
            </motion.div>
          )}

          {/* Status indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-4 text-sm text-white/50"
          >
            {state === 'listening' && '🎤 Recording... Tap Stop when done'}
            {state === 'processing' && '⏳ Processing...'}
            {state === 'speaking' && '🔊 Niki is speaking...'}
            {state === 'connecting' && '🔗 Connecting microphone...'}
          </motion.div>

          {/* Transcript */}
          <AnimatePresence>
            {transcript && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="mt-6 max-w-lg px-6 w-full"
              >
                <div className="glass-card p-4">
                  <p className="text-xs text-white/50 mb-2">
                    {state === 'listening' || state === 'processing' ? 'You said:' : 'Niki:'}
                  </p>
                  <p className="text-white text-lg leading-relaxed">{transcript}</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Stop Button */}
          <motion.button
            onClick={onStop}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="mt-8 px-8 py-4 bg-danger text-white rounded-xl font-semibold shadow-lg shadow-danger/40"
          >
            Stop Listening
          </motion.button>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  )
}
